import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import type { MaterialType } from "@tfg/db"

import { materialTypeUpdateValidationType } from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useCreateMaterialTypeMutation = (
  options?: UseMutationOptions<
    MaterialType,
    unknown,
    materialTypeUpdateValidationType
  >
) =>
  useMutation<MaterialType, unknown, materialTypeUpdateValidationType>({
    ...options,
    mutationFn: async (materialType: materialTypeUpdateValidationType) => {
      const res = await httpClient.post<MaterialType>(
        "/admin/materialTypes",
        materialType
      )
      return res.data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({ queryKey: ["materialTypes"] })
      showSuccessNotification({
        title: "Added",
        message: "Successfully added material type",
      })
    },
  })

export const useUpdateMaterialTypeMutation = (
  options?: UseMutationOptions<
    MaterialType,
    unknown,
    { materialId: number; materialType: materialTypeUpdateValidationType }
  >
) =>
  useMutation<
    MaterialType,
    unknown,
    { materialId: number; materialType: materialTypeUpdateValidationType }
  >({
    ...options,
    mutationFn: async ({ materialId, materialType }) => {
      const res = await httpClient.patch<MaterialType>(
        `/admin/materialTypes/${materialId}`,
        materialType
      )
      return res.data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({ queryKey: ["materialTypes"] })
      showSuccessNotification({
        title: "Added",
        message: `Successfully updated material type : ${variables.materialType.key}`,
      })
    },
  })

export const useSyncMaterialTypesWithGoogleSheetMutation = (
  options?: UseMutationOptions<boolean, unknown, void>
) =>
  useMutation<boolean, unknown, void>({
    ...options,
    mutationFn: async () => {
      const res = await httpClient.post<boolean>("/admin/materialTypes/sync")
      return res.data
    },
  })

export const useDeleteMaterialTypeMutation = (
  options?: UseMutationOptions<void, unknown, number>
) => {
  return useMutation({
    ...options,
    mutationFn: async (materialId) => {
      return httpClient.delete(`/admin/materialtypes/${materialId}`)
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      showSuccessNotification({
        title: "Deleted",
        message: "Material type has been deleted",
      })
      await queryClient.invalidateQueries({ queryKey: ["materialTypes"] })
    },
  })
}
